import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import Breadcrumbs from '../../components/Breadcrumbs';
import { FieldOwnerIcon, FieldLevelIcon, RocketIcon, ArrowLeftIcon } from '../../assets/icons';

const INPUT =
  'w-full rounded-md border border-line bg-white px-[13px] py-3 text-base text-ink shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] placeholder:text-[#6b7280] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

const OWNERS = ['Rajesh Kumar', "Sarah D'Souza", 'Amit Shahane', 'Lata Hegde'];
const LEVELS = ['National', 'State', 'District', 'Constituency', 'Booth'];

interface EngagementFormValues {
  name: string;
  date: string;
  boundary: string;
  owner: string;
  level: string;
  status: 'draft' | 'active' | 'closed';
  description: string;
}

// Validation wired but errors intentionally not surfaced ("hide validations").
const validationSchema = Yup.object({
  name: Yup.string().required('Engagement name is required'),
  owner: Yup.string().required('Engagement owner is required'),
});

function Card({ eyebrow, children }: { eyebrow: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-line-soft bg-white shadow-card">
      <div className="border-b border-[#f3f4f6] px-6 py-5">
        <span className="text-xs font-semibold uppercase tracking-[0.6px] text-[#6b7280]">
          {eyebrow}
        </span>
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function Label({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <span className="text-sm font-medium text-[#374151]">
      {children}
      {required && <span className="text-[#ef4444]"> *</span>}
    </span>
  );
}

export default function CreateEngagementPage() {
  const navigate = useNavigate();

  const formik = useFormik<EngagementFormValues>({
    initialValues: {
      name: '',
      date: '',
      boundary: '',
      owner: '',
      level: '',
      status: 'active',
      description: '',
    },
    validationSchema,
    onSubmit: (values) => {
      // TODO: wire to a createEngagement mutation once the API exists.
      // eslint-disable-next-line no-console
      console.log('Create engagement', values);
      navigate('/user/engagements');
    },
  });

  const { values, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched } = formik;

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6" data-testid="create-engagement">
      {/* Heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[{ label: 'Engagements', to: '/user/engagements' }, { label: 'Create Engagement' }]}
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Back to engagements"
            onClick={() => navigate('/user/engagements')}
            className="text-ink-900 transition-colors hover:text-brand"
          >
            <ArrowLeftIcon className="size-5" />
          </button>
          <h1 className="text-2xl font-semibold tracking-[-0.4px] text-ink">Create Engagement</h1>
        </div>
      </div>

      {/* Basic information */}
      <Card eyebrow="Basic Information">
        <div className="flex flex-col gap-6">
          <label className="flex flex-col gap-2">
            <Label required>Engagement Name</Label>
            <input
              name="name"
              placeholder="Enter engagement name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={INPUT}
            />
          </label>

          <div className="flex flex-col gap-6 sm:flex-row">
            <label className="flex flex-1 flex-col gap-2">
              <Label>Date</Label>
              <DatePicker
                className="w-full"
                format="MM/DD/YYYY"
                placeholder="mm/dd/yyyy"
                value={values.date ? dayjs(values.date) : null}
                onChange={(d) => setFieldValue('date', d ? d.format('YYYY-MM-DD') : '')}
                onBlur={() => setFieldTouched('date', true)}
              />
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <Label>Operational Boundary</Label>
              <input
                name="boundary"
                placeholder="ORG Level Selection"
                value={values.boundary}
                onChange={handleChange}
                onBlur={handleBlur}
                className={INPUT}
              />
            </label>
          </div>

          <div className="flex flex-col gap-6 sm:flex-row">
            <div className="flex flex-1 flex-col gap-2">
              <Label required>Engagement Owner</Label>
              <Select
                className="w-full"
                placeholder="Search and select owner"
                showSearch
                prefix={<FieldOwnerIcon className="size-5 text-[#9ca3af]" />}
                value={values.owner || undefined}
                onChange={(v) => setFieldValue('owner', v)}
                onBlur={() => setFieldTouched('owner', true)}
                options={OWNERS.map((o) => ({ value: o, label: o }))}
              />
              <span className="text-xs text-[#6b7280]">
                Assign a Regional Director or Lead Coordinator for this engagement.
              </span>
            </div>

            <div className="flex flex-1 flex-col gap-2">
              <Label>Level</Label>
              <Select
                className="w-full"
                placeholder="Select level"
                prefix={<FieldLevelIcon className="h-5 w-4 text-[#9ca3af]" />}
                value={values.level || undefined}
                onChange={(v) => setFieldValue('level', v)}
                onBlur={() => setFieldTouched('level', true)}
                options={LEVELS.map((l) => ({ value: l, label: l }))}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Engagement status */}
      <Card eyebrow="Engagement Status">
        <div className="flex items-center">
          {(['draft', 'active', 'closed'] as const).map((s, i) => (
            <button
              key={s}
              type="button"
              onClick={() => setFieldValue('status', s)}
              className={['flex items-center gap-2', i > 0 ? 'pl-8' : ''].join(' ')}
            >
              <span
                className={[
                  'flex size-[18px] items-center justify-center rounded-full border transition-colors',
                  values.status === s ? 'border-[#003da5] bg-[#003da5]' : 'border-[#cbd5e1] bg-white',
                ].join(' ')}
              >
                {values.status === s && <span className="size-2.5 rounded-full bg-white" />}
              </span>
              <span className="text-sm capitalize text-[#374151]">{s}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Description */}
      <Card eyebrow="Description">
        <label className="flex flex-col gap-2">
          <Label>Engagement Description</Label>
          <textarea
            name="description"
            rows={5}
            placeholder="Enter engagement description..."
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            className={[INPUT, 'resize-none placeholder:text-[#9ca3af]'].join(' ')}
          />
        </label>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap justify-end gap-4">
        <button
          type="button"
          onClick={() => navigate('/user/engagements')}
          className="rounded-lg border border-brand px-8 py-3 text-base text-brand transition-colors hover:bg-brand-shade"
        >
          Save Draft
        </button>
        <button
          type="submit"
          className="flex items-center gap-2 rounded-lg bg-brand px-8 py-3 text-base text-white shadow-[0px_10px_15px_-3px_rgba(0,61,155,0.2),0px_4px_6px_-4px_rgba(0,61,155,0.2)] transition-colors hover:bg-brand-dark"
        >
          Create Engagement
          <RocketIcon className="size-[15px]" />
        </button>
      </div>
    </form>
  );
}
